export function isSafeAppRedirect(path) {
    return typeof path === 'string' &&
        path.startsWith('/') &&
        !path.startsWith('//');
}

function isDashboardRedirect(path, route) {
    if (route?.name === 'dashboard') {
        return true;
    }
    if (typeof path !== 'string') {
        return false;
    }
    return path === '/dashboard' || path.startsWith('/dashboard?');
}

const SETUP_QUERY_KEYS = ['step', 'redirect', 'code', 'error'];

function copySetupQuery(query, source) {
    if (!source) {
        return;
    }
    SETUP_QUERY_KEYS.forEach((key) => {
        if (query[key] != null && query[key] !== '') {
            return;
        }
        if (source[key] != null && source[key] !== '') {
            query[key] = source[key];
        }
    });
}

export function isSetupPath(path) {
    return typeof path === 'string' &&
        (path === '/setup' || path.startsWith('/setup?'));
}

// setup_step is the last completed wizard step (1 business, 2 strategy, 3 done).
// Resume on the next incomplete step.
export function resumeSetupStep(project) {
    const completed = Number(project?.setup_step);
    if (completed === 1 || completed === 2) {
        return completed + 1;
    }
    if (completed === 3) {
        return 3;
    }
    return 1;
}

export function setupExitTarget(redirect) {
    if (
        isSafeAppRedirect(redirect) &&
        !isSetupPath(redirect) &&
        !isDashboardRedirect(redirect)
    ) {
        return { path: redirect };
    }
    return { name: 'dashboard' };
}

function setupQueryFromPath(path) {
    if (!isSetupPath(path)) {
        return null;
    }
    const query_index = path.indexOf('?');
    if (query_index < 0) {
        return {};
    }
    const params = new URLSearchParams(path.slice(query_index + 1));
    const query = {};
    SETUP_QUERY_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) {
            query[key] = value;
        }
    });
    return query;
}

export function setupRoute({ from, to, fullPath, query: extra = {} } = {}) {
    const query = { ...extra };
    let intended = fullPath || (
        to?.name && to.name !== 'setup' && to.name !== 'logout' ? to.fullPath : null
    );

    // Prefer an explicit setup route (`from` / `to`), then a setup `fullPath`.
    if (from?.name === 'setup') {
        copySetupQuery(query, from.query);
    } else if (to?.name === 'setup') {
        copySetupQuery(query, to.query);
    } else {
        copySetupQuery(query, setupQueryFromPath(fullPath));
    }

    // Never bounce setup → setup via redirect=.
    if (isSetupPath(intended)) {
        intended = null;
    }

    if (!query.redirect) {
        // finish() already defaults to dashboard — don't clutter the setup URL.
        if (
            isSafeAppRedirect(intended) &&
            !isSetupPath(intended) &&
            !isDashboardRedirect(intended, to)
        ) {
            query.redirect = intended;
        }
    } else if (isDashboardRedirect(query.redirect) || isSetupPath(query.redirect)) {
        delete query.redirect;
    }

    return Object.keys(query).length ? { name: 'setup', query } : { name: 'setup' };
}

export function clearSetupOAuthQuery(route, step = '3') {
    const query = {
        ...route.query,
        step: String(step),
    };
    delete query.code;
    delete query.error;
    if (isDashboardRedirect(query.redirect) || isSetupPath(query.redirect)) {
        delete query.redirect;
    }
    return { name: 'setup', query };
}
